import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { client } from '../../client';
import type { ImageType } from '../../generated';

/*
 * Hand-written stand-in for the benchmark list endpoint, which the API does
 * not expose yet. Everything a caller needs (the types, the hook, its getKey
 * and fetcher) lives in this one file, mirroring the shape of the generated
 * wrappers. When the endpoint lands, regenerate the API and point this file at
 * the generated SDK function and types; callers stay untouched.
 *
 * The URL and fields below are provisional and agreed with nobody yet. Tests
 * answer this path with the MSW handler in `tests/mocks/handlers.ts`.
 */

const SEARCH_BENCHMARKS_URL = '/v1/workspaces/{workspace_id}/benchmarks';

export type BenchmarkSortBy = 'created_at' | 'name';

export type BenchmarkSortDirection = 'asc' | 'desc';

export interface BenchmarkCreatorView {
  id: string;
  name: string;
}

export interface BenchmarkView {
  id: string;
  name: string;
  description: string | null;
  /** Hero or Carousel, chosen at creation and locked afterwards. */
  scope: ImageType;
  product_category_id: string | null;
  product_category_name: string | null;
  image_count: number;
  /** Up to four thumbnail URLs for the card mosaic, oldest image first. */
  preview_image_urls: string[];
  created_by: BenchmarkCreatorView;
  created_at: string;
}

export interface SearchBenchmarksQueryVariables {
  workspace_id: string;
  name_like?: string;
  product_category_id?: string;
  sort_by?: BenchmarkSortBy;
  sort_direction?: BenchmarkSortDirection;
}

export type SearchBenchmarksQuery = BenchmarkView[];

async function searchBenchmarks({ workspace_id, ...query }: SearchBenchmarksQueryVariables) {
  const { data } = await client.get<{ 200: SearchBenchmarksQuery }, unknown, true>({
    url: SEARCH_BENCHMARKS_URL,
    path: { workspace_id },
    query,
    throwOnError: true,
  });
  return data;
}

export const useSearchBenchmarksQuery = <TData = SearchBenchmarksQuery, TError = unknown>(
  variables: SearchBenchmarksQueryVariables,
  options?: Omit<UseQueryOptions<SearchBenchmarksQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchBenchmarksQuery, TError, TData>({
    queryKey: useSearchBenchmarksQuery.getKey(variables),
    queryFn: () => searchBenchmarks(variables),
    ...options,
  });
};

useSearchBenchmarksQuery.getKey = (variables: SearchBenchmarksQueryVariables) => ['SearchBenchmarks', variables];

useSearchBenchmarksQuery.fetcher = (variables: SearchBenchmarksQueryVariables) => () => searchBenchmarks(variables);
